import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { CustomerMarketing } from "@/types/sms";
import { fetchMarketingExclusions } from "@/hooks/sms/useMarketingExclusion";

/**
 * Recency segment, mirroring v_customer_lifecycle's day-since-last-order
 * thresholds as documented in RevenuePlays.tsx: active <30, cooling 30-60,
 * at_risk 60-90, lapsing 90-180, lost >=180.
 */
export type RecencySegment = "active" | "cooling" | "at_risk" | "lapsing" | "lost";

export function recencySegment(lastOrderAt: string | null, now: Date = new Date()): RecencySegment {
  if (!lastOrderAt) return "lost";
  const days = (now.getTime() - new Date(lastOrderAt).getTime()) / 86_400_000;
  if (days < 30) return "active";
  if (days < 60) return "cooling";
  if (days < 90) return "at_risk";
  if (days < 180) return "lapsing";
  return "lost";
}

/** The audience types this launch UI supports. "lurkers" narrows further by
 *  a specific lifecycle segment (cooling/at_risk/lapsing). */
export type LaunchAudienceType = "everyone" | "active" | "lurkers";

export interface AudienceSelection {
  audienceType: LaunchAudienceType;
  /** Required when audienceType === "lurkers". */
  lurkerSegment?: "cooling" | "at_risk" | "lapsing";
}

export interface AudienceEstimate {
  count: number;
  phones: string[];
}

/** Live subscriber counts per composer audience chip, from one fetch. */
export interface AudienceBreakdown {
  counts: {
    everyone: number;
    active: number;
    cooling: number;
    at_risk: number;
    lapsing: number;
  };
  members: Pick<CustomerMarketing, "customer_phone" | "last_order_at">[];
}

const PAGE_SIZE = 500;

/** The sendable-subscriber base query (opted_in + enabled, not deleted, not
 *  excluded from marketing texts by the owner), paged so PostgREST's max-rows
 *  cap can never truncate it — the same base the engine's resolveAudience
 *  sends with (it skips excluded customers as `marketing_excluded`). */
async function fetchAudienceMembers(sb: SupabaseClient): Promise<CustomerMarketing[]> {
  const members: CustomerMarketing[] = [];
  let from = 0;
  while (true) {
    const { data, error } = await sb
      .from("v_customer_marketing")
      .select("customer_phone, last_order_at, opt_in_status, messaging_status, deleted_at")
      .eq("opt_in_status", "opted_in")
      .eq("messaging_status", "enabled")
      .order("customer_phone", { ascending: true })
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    const page = (data ?? []) as CustomerMarketing[];
    members.push(...page);
    if (page.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  const exclusions = await fetchMarketingExclusions(sb);
  return members.filter((m) => !m.deleted_at && !exclusions.byPhone.has(m.customer_phone));
}

/** Filters fetched members down to an audience selection — the one shared
 *  implementation behind both the segment counts and the launch phone list,
 *  so the number on the chip is always the number that gets texted. */
export function filterBySelection(
  members: Pick<CustomerMarketing, "customer_phone" | "last_order_at">[],
  selection: AudienceSelection,
  now: Date = new Date(),
): Pick<CustomerMarketing, "customer_phone" | "last_order_at">[] {
  return members.filter((m) => {
    if (selection.audienceType === "everyone") return true;
    const segment = recencySegment(m.last_order_at, now);
    if (selection.audienceType === "active") return segment === "active";
    return segment === (selection.lurkerSegment ?? "lapsing");
  });
}

/** One fetch, every chip count: the composer's Audience card reads this. */
export const useAudienceBreakdown = () => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<AudienceBreakdown>({
    queryKey: ["sms-campaign-audience-breakdown"],
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const members = await fetchAudienceMembers(sb);
      const now = new Date();
      const counts = { everyone: members.length, active: 0, cooling: 0, at_risk: 0, lapsing: 0 };
      for (const m of members) {
        const segment = recencySegment(m.last_order_at, now);
        if (segment === "active") counts.active += 1;
        else if (segment === "cooling") counts.cooling += 1;
        else if (segment === "at_risk") counts.at_risk += 1;
        else if (segment === "lapsing") counts.lapsing += 1;
      }
      return { counts, members };
    },
    staleTime: 60 * 1000,
  });
};

/**
 * Resolves an audience selection into the exact phone list that would be
 * targeted, using the same base query as sms-jobs/actions/campaigns.ts's
 * resolveAudience (v_customer_marketing, opted_in + enabled). The live
 * engine's resolveAudience only actually narrows by segment when
 * audience_type = 'specific' with an explicit phones list (its 'everyone' /
 * 'active' / 'lurkers' branches all fall through to the same
 * opted_in+enabled query) — so this hook computes the segment-narrowed
 * phone list client-side and the campaign is always created with
 * audience_type: 'specific' whenever a segment is chosen, to guarantee the
 * campaign actually reaches only the audience shown in the estimate. Only
 * a true "everyone" selection uses the native 'everyone' audience_type,
 * since that already matches the engine's default behavior exactly.
 */
export const useAudienceEstimate = (selection: AudienceSelection) => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<AudienceEstimate>({
    queryKey: ["sms-campaign-audience-estimate", selection],
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const members = await fetchAudienceMembers(sb);
      const filtered = filterBySelection(members, selection);
      return { count: filtered.length, phones: filtered.map((m) => m.customer_phone) };
    },
    staleTime: 60 * 1000,
  });
};

export interface NewCampaignInput {
  /** Pre-generated so the composer's holdout count uses the same id the engine hashes. */
  campaignId: string;
  storeId: string;
  name: string;
  promotionId: string;
  messageBody: string;
  imageUrl: string | null;
  expirationDays: number | null;
  timerMinutes: number | null;
  holdoutPct: number;
  effectiveSendAt: string;
  selection: AudienceSelection;
  phones: string[];
}

/** Creates a `marketing_campaigns` row, status='scheduled' so the sms-jobs
 *  `campaigns` action picks it up at effective_send_at (or immediately, if
 *  that time has already passed). Never writes campaign_recipients — those
 *  are edge-only, materialised by the worker. */
export const useCreateCampaign = () => {
  const getSupabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: NewCampaignInput) => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const audienceType = input.selection.audienceType === "everyone" ? "everyone" : "specific";
      const audienceFilter =
        audienceType === "specific"
          ? {
              phones: input.phones,
              source_audience_type: input.selection.audienceType,
              source_segment: input.selection.lurkerSegment ?? null,
            }
          : null;

      const { data, error } = await sb
        .from("marketing_campaigns")
        .insert({
          id: input.campaignId,
          store_id: input.storeId,
          name: input.name,
          play: "custom",
          channel: "sms",
          type: "promo",
          status: "scheduled",
          audience_type: audienceType,
          audience_filter: audienceFilter,
          promotion_id: input.promotionId,
          message_body: input.messageBody,
          image_url: input.imageUrl,
          expiration_days: input.expirationDays,
          timer_minutes: input.timerMinutes,
          holdout_pct: input.holdoutPct,
          send_at: input.effectiveSendAt,
          effective_send_at: input.effectiveSendAt,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sms-campaigns"] });
    },
  });
};
